import {
  beginViewerNavigation,
  isCurrentViewerNavigation,
} from "../FileViewer/services/viewerNavigation";
import { openInteractionInViewer } from "../FpPages/RMS/Dashboard/Cards/openInViewer";
import React, { useEffect, useState } from "react";
import { ReferenceType } from "../FpAppV4/utils/fpAppV4Interface";
import { useDispatch } from "react-redux";
import { Popover } from "@mui/material";
import { Telescope } from "lucide-react";
import { getSourceRefs } from "./services/getSourceRefs";
import CustomPopover from "../common/CustomPopover";
import { setFileViewerData } from "@/redux/fileViewerSlice";
import { getViewerMode } from "../FpPages/viewerMode";

const ListenCiteReference = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLSpanElement | null>(null);
  const [multipleCitationPopoverAnchorEl, setMultipleCitationPopoverAnchorEl] =
    useState<HTMLSpanElement | null>(null);
  const [citationsRefs, setCitationsRefs] = useState<ReferenceType[]>([]);
  const [sourceRefs, setSourceRefs] = useState<ReferenceType[]>([]);
  const [refDetails, setRefDetails] = useState<{
    originalNumValue?: string;
    qid?: string;
    numValue?: string;
    parentElementText?: string;
  }>({});

  const dispatch = useDispatch();

  const clickRef = (
    ref: ReferenceType,
    metaDetails?: {
      originalNumValue?: string;
      qid?: string;
      numValue?: string;
      parentElementText?: string;
    }
  ) => {
    if ("interactionId" in ref && ref.interactionId) {
      void openInteractionInViewer(ref.interactionId, ref.docTitle, {
        highlightContext: ref.smallContext || ref.content,
        originalNumValue: metaDetails?.originalNumValue || ref.originalNumValue,
        parentElementText: metaDetails?.parentElementText,
      });
      setRefDetails({});
      return;
    }
    // Dispatch the openDocumentPanel event so Layout.tsx can handle sidebar closing
    const eventDetail = {
      citationId: ref.displaySrcNum?.toString() || "",
      queryId: metaDetails?.qid || refDetails.qid,
      originalNumValue:
        metaDetails?.originalNumValue ||
        refDetails.originalNumValue ||
        ref.originalNumValue,
      parentElementText:
        metaDetails?.parentElementText || refDetails.parentElementText,
      fileId: ref.parentDocId,
      fileName:
        ref.blobFilename ||
        ref.docTitle ||
        ref.parentDoc?.docTitle ||
        "Unknown Document",
      fileType: ref.blobType || "pdf",
      highlightContext: ref.smallContext || ref.content,
      highlightPageNum: ref.pageNum,
    };
    window.dispatchEvent(
      new CustomEvent("openDocumentPanel", {
        detail: eventDetail,
      })
    );

    // Also update Redux state for backward compatibility
    dispatch(
      setFileViewerData({
        isOpen: true,
        highlightContext: ref.smallContext || ref.content,
        highlightPageNum: ref.pageNum,
        originalNumValue:
          metaDetails?.originalNumValue ||
          refDetails.originalNumValue ||
          ref.originalNumValue,
        parentElementText:
          metaDetails?.parentElementText || refDetails.parentElementText,
        fileId: ref.parentDocId,
        fileName: ref.blobFilename || ref.docTitle || ref.parentDoc?.docTitle,
        fileType: ref.blobType,
        // The flag follows the surface's viewer mode, as Layout's own funnel
        // does (#11975): an editor citation fires both openers, and an
        // unconditional `true` here told the overlay drawer the document
        // Layout had just opened in it was closing.
        doNotOpenDrawer: getViewerMode(window.location.pathname) !== "overlay",
      })
    );
    setRefDetails({});
  };

  const handleMultipleCitationsClick = async (
    e: React.MouseEvent<HTMLSpanElement>,
    details: {
      qid: string;
      multipleCitations?: string;
      originalNumValue?: string;
      parentElementText?: string;
    }
  ) => {
    const intent = beginViewerNavigation();
    const citations = details.multipleCitations?.split(",");
    const citationsRefs = [];

    for (const citation of citations || []) {
      const refs = await getSourceRefs(details.qid, `[${citation.trim()}]`);
      citationsRefs.push(refs[0]);
    }
    if (!isCurrentViewerNavigation(intent)) return;
    setRefDetails({
      qid: details.qid,
      originalNumValue: details.originalNumValue,
      parentElementText: details.parentElementText,
    });
    setCitationsRefs(citationsRefs);

    // Use the target element directly since it's a DOM element from MultipleCitationsNode
    const targetElement = e.target as HTMLSpanElement;
    setMultipleCitationPopoverAnchorEl(targetElement);
  };

  const handleReferenceClick = async (
    e: React.MouseEvent<HTMLSpanElement>,
    details: {
      originalNumValue?: string;
      qid: string;
      numValue?: string;
      parentElementText?: string;
      id?: string;
      multipleCitations?: string;
      regexNumberUsingSource?: {
        originalNumValue: string;
        numValue: string;
      };
    }
  ) => {
    if (details.multipleCitations && details.qid) {
      handleMultipleCitationsClick(e, details);
      return;
    }
    const intent = beginViewerNavigation();
    let sourceRefs: ReferenceType[] = [];
    if (details.regexNumberUsingSource && details.id) {
      sourceRefs = await getSourceRefs(
        details.qid,
        details.id,
        details.regexNumberUsingSource.numValue
      );
    } else if (details.qid && details.id) {
      sourceRefs = await getSourceRefs(details.qid, details.id);
    } else if (details.qid && details.numValue) {
      sourceRefs = await getSourceRefs(details.qid, details.numValue);
    }
    if (!isCurrentViewerNavigation(intent)) return;
    const length = sourceRefs.length;
    if (length > 1) {
      setAnchorEl(e.target as HTMLSpanElement);
      setSourceRefs(sourceRefs);
      setRefDetails(details);
    }
    if (length === 1) {
      const ref = sourceRefs[0];
      clickRef(ref, details);
    }
    if (length === 0) {
      setRefDetails({});
      console.log("no refs found for ", details);
    }
  };

  useEffect(() => {
    (window as any).handleReferenceClick = handleReferenceClick;
  }, [handleReferenceClick]);

  return (
    <>
      <CustomPopover
        id={anchorEl ? "simple-popover" : undefined}
        open={anchorEl !== null}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          dispatch(setFileViewerData({ isOpen: false }));
          setRefDetails({});
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <div className="p-1 border-gray-300 border-2 pt-0 max-h-[300px] overflow-y-auto bg-gray-200">
          {sourceRefs.map((ref, index) => (
            <div
              key={index}
              onClick={() => {
                setAnchorEl(null);
                clickRef(ref);
              }}
              className="border border-gray-300 w-[400px] p-2 max-h-[120px] mt-1 cursor-pointer bg-gray-100 text-gray-600 rounded-md transition-all duration-300"
            >
              <div className="text-gray-600 text-[12px] font-bold">
                {ref.docTitle} : (Pg{ref.pageNum})
              </div>
              <div className="text-[10px] w-full h-[46px]  line-clamp-3">
                {findHelpfulContext(
                  refDetails,
                  ref.smallContext || ref.content || ""
                )}
              </div>
              <div className="text-[10px] text-gray-500 mt-1">
                <Telescope className="inline-block mr-1 w-[12px]" />
                Open Source
              </div>
            </div>
          ))}
        </div>
      </CustomPopover>
      <Popover
        id={
          multipleCitationPopoverAnchorEl
            ? "multiple-citation-popover"
            : undefined
        }
        open={multipleCitationPopoverAnchorEl !== null}
        anchorEl={multipleCitationPopoverAnchorEl}
        onClose={() => {
          setMultipleCitationPopoverAnchorEl(null);
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        disableRestoreFocus
        disableScrollLock
      >
        <div
          className="p-1 border-gray-300 border-2 pt-0 max-h-[300px] max-w-[400px] overflow-y-auto bg-gray-200 cursor-pointer"
          onClick={() => {
            // console.log(citationsRefs);
          }}
        >
          {citationsRefs.filter(Boolean).map((citationRefs, index) => (
            <div
              key={index}
              className="border-b border-gray-300 bg-white p-2 border rounded-md m-1 text-[10px] max-h-[100px] overflow-clip"
              onClick={() => {
                setMultipleCitationPopoverAnchorEl(null);
                clickRef(citationRefs, {
                  qid: refDetails.qid,
                  originalNumValue: refDetails.originalNumValue,
                  parentElementText: refDetails.parentElementText,
                });
              }}
            >
              <div className="text-[12px] font-bold">
                Source: {citationRefs?.displaySrcNum}
              </div>
              <div className="text-[12px] mt-2">
                {citationRefs?.docTitle || citationRefs?.parentDoc?.docTitle} :
                (Pg{citationRefs?.pageNum})
              </div>
              <div className="text-[12px] text-gray-500">
                <Telescope className="inline-block mr-1 w-[12px]" />
                Open Source
              </div>
            </div>
          ))}
        </div>
      </Popover>
    </>
  );
};

export default ListenCiteReference;

function findHelpfulContext(
  details: {
    originalNumValue?: string;
    qid?: string;
    numValue?: string;
    parentElementText?: string;
  },
  smallContext: string
) {
  const { originalNumValue } = details;
  if (originalNumValue === undefined) {
    return smallContext;
  }

  const indices = findAllIndicesOf(smallContext, originalNumValue);
  if (indices.length === 0) return smallContext;

  const WINDOW_SIZE = 40;
  const numLength = String(originalNumValue).length;

  // First, create initial windows
  let windows = indices.map((index) => ({
    start: Math.max(0, index - WINDOW_SIZE),
    end: Math.min(smallContext.length, index + WINDOW_SIZE + numLength),
  }));

  // Merge overlapping windows
  let mergedWindows = [];
  let currentWindow = windows[0];

  for (let i = 1; i < windows.length; i++) {
    if (windows[i].start <= currentWindow.end) {
      // Windows overlap, merge them
      currentWindow.end = Math.max(currentWindow.end, windows[i].end);
    } else {
      // No overlap, start new window
      mergedWindows.push(currentWindow);
      currentWindow = windows[i];
    }
  }
  mergedWindows.push(currentWindow);
  console.log("mergedWindows", mergedWindows);
  return (
    <>
      {mergedWindows.map((window, windowIndex) => {
        const text = smallContext.slice(window.start, window.end);
        const parts = text.split(String(originalNumValue));

        return (
          <React.Fragment key={`window-${windowIndex}`}>
            {parts.length > 1 ? (
              <span>
                {parts[0]}
                <b>{String(originalNumValue)}</b>
                {parts[1]}
              </span>
            ) : (
              <span>{text}</span>
            )}
            <br />
          </React.Fragment>
        );
      })}
    </>
  );
}

type WindowEpic = {
  startIndex: number;
  endIndex: number;
  occurrences: number[];
};

function mergeOverlappingWindows(
  indices: number[],
  windowSize: number,
  stringLength: number,
  valueLength: number
): WindowEpic[] {
  if (indices.length === 0) return [];

  // Sort indices to ensure we process them in order
  indices.sort((a, b) => a - b);

  const windows: WindowEpic[] = [];
  let currentWindow: WindowEpic = {
    startIndex: Math.max(0, indices[0] - windowSize),
    endIndex: Math.min(stringLength, indices[0] + windowSize + valueLength),
    occurrences: [indices[0]],
  };

  for (let i = 1; i < indices.length; i++) {
    const currentIndex = indices[i];
    const potentialStartIndex = Math.max(0, currentIndex - windowSize);
    const potentialEndIndex = Math.min(
      stringLength,
      currentIndex + windowSize + valueLength
    );

    // Check if this index's window overlaps with the current window
    if (potentialStartIndex <= currentWindow.endIndex) {
      // Merge by extending the current window
      currentWindow.endIndex = Math.max(
        currentWindow.endIndex,
        potentialEndIndex
      );
      currentWindow.occurrences.push(currentIndex);
    } else {
      // No overlap, create new window
      windows.push(currentWindow);
      currentWindow = {
        startIndex: potentialStartIndex,
        endIndex: potentialEndIndex,
        occurrences: [currentIndex],
      };
    }
  }

  // Don't forget to add the last window
  windows.push(currentWindow);

  return windows;
}

function findAllIndicesOf(string: string, substring: string): number[] {
  const indices = [];
  let index = -1;
  while (1) {
    index = string.indexOf(substring, index + 1);
    if (index === -1) return indices;
    indices.push(index);
  }
  return indices;
}
